import { createWithdrawInstruction } from '@metaplex-foundation/mpl-candy-guard';
import { useOperation } from '../../../types/Operation.mjs';
import { TransactionBuilder } from '../../../utils/TransactionBuilder.mjs';

// -----------------
// Operation
// -----------------

const Key = 'DeleteCandyGuardOperation';

/**
 * Deletes a Candy Guard account by withdrawing its rent-exempt balance.
 *
 * ```ts
 * await metaplex
 *   .candyMachines()
 *   .deleteCandyGuard({
 *     candyGuard,
 *     authority,
 *   };
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const deleteCandyGuardOperation = useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const deleteCandyGuardOperationHandler = {
  async handle(operation, metaplex, scope) {
    return deleteCandyGuardBuilder(metaplex, operation.input, scope).sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Deletes a Candy Guard account by withdrawing its rent-exempt balance.
 *
 * ```ts
 * const transactionBuilder = await metaplex
 *   .candyMachines()
 *   .builders()
 *   .deleteCandyGuard({
 *     candyGuard,
 *     authority,
 *   });
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const deleteCandyGuardBuilder = (metaplex, params, options = {}) => {
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    candyGuard,
    authority = metaplex.identity()
  } = params;
  const candyGuardProgram = metaplex.programs().getCandyGuard(programs);
  return TransactionBuilder.make().setFeePayer(payer).add({
    instruction: createWithdrawInstruction({
      candyGuard,
      authority: authority.publicKey
    }, candyGuardProgram.address),
    signers: [authority],
    key: params.deleteCandyGuardInstructionKey ?? 'deleteCandyGuard'
  });
};

export { deleteCandyGuardBuilder, deleteCandyGuardOperation, deleteCandyGuardOperationHandler };
//# sourceMappingURL=deleteCandyGuard.mjs.map
